/**
 * Iniciar projeto — from an idea to the first code, in a conversation (docs/refactor/13-iniciar-projeto.md).
 *
 * Four steps with human names: "Sua ideia", "Algumas perguntas", "A proposta", "Começar". Every AI
 * step is a catalog prompt (start.interview, start.proposal, start.review, start.docs, start.first)
 * sent as a work order, so it runs on the person's own agent — the desktop app's runner or the IDE
 * at home — exactly like the header's buttons, and is followed the same way (lib/repositoryRun.ts).
 *
 * "Começar" in the desktop app makes the folder where the person picks (a git repository with a
 * first commit, desktop/src-tauri/src/workspace.rs project_create), links it in Vephus, writes the
 * documentation and starts the first specification. In a browser no folder can be made: an already
 * linked repository can be used (the agent at home does the rest), otherwise the person finishes in
 * the desktop app, where the saved interview is waiting under "Continuar".
 *
 * What is kept: lib/startProject.ts.
 */

import { askSensitive } from '../components/SensitiveStep'
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Check, CheckCheck, ClipboardList, FolderPlus, Hourglass, Loader2, RotateCcw, Send, Sparkles, Square } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { githubAccount, isDesktop, projectCreate, projectPublishGithub } from '../lib/desktop'
import { useI18n } from '../lib/i18n'
import { localized, usePromptCatalog } from '../lib/prompts'
import { useRepositoryRun } from '../lib/repositoryRun'
import { useActiveRepository } from '../lib/activeRepository'
import { folderFor, rememberFolder } from '../lib/repoFolders'
import { ensurePendingList } from '../lib/pendingList'
import {
  answerOne,
  answersText,
  assumedCount,
  clearDraft,
  confirmAll,
  confirmOne,
  dontKnow,
  draftFromDoc,
  emptyDraft,
  firstSpecOf,
  fitPrompt,
  initialAnswers,
  interviewDoc,
  interviewDocPath,
  interviewOf,
  isInterviewDoc,
  loadDraft,
  pendingItems,
  proposalDoc,
  proposalDocPath,
  proposalOf,
  reviewOf,
  saveDraft,
  skipOne,
  slugify,
  unresolved,
  validProjectName,
  type Answer,
  type InterviewQuestion,
  type StartDraft,
  type StartStep,
  type Waiting,
} from '../lib/startProject'
import type { RepositoryLink, WorkOrder } from '../lib/types'
import { Markdown } from '../components/Markdown'

const STEPS: StartStep[] = ['idea', 'questions', 'proposal', 'start']
const PAGE = 5
/** The key the followed order is kept under for this tab (lib/repositoryRun.ts). */
const RUN_KEY = 'start-project'

export default function StartProject() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const { data: catalog } = usePromptCatalog()
  const { linked, choose } = useActiveRepository()
  const [draft, setDraft] = useState<StartDraft>(loadDraft)
  const [problem, setProblem] = useState<string | null>(null)
  const [githubNote, setGithubNote] = useState<string | null>(null)
  const latest = useRef(draft)
  latest.current = draft

  const set = useCallback((next: StartDraft) => {
    latest.current = next
    saveDraft(next)
    setDraft(next)
  }, [])
  const patch = useCallback((change: Partial<StartDraft>) => set({ ...latest.current, ...change }), [set])

  /** The interview and the proposal in the team's documentation: to resume, and for the team to read. */
  const persist = useCallback(async (d: StartDraft) => {
    if (!d.name) return
    try {
      await api.docs.upsertByPath(interviewDocPath(d.name), interviewDoc(d, t))
      if (d.proposal) await api.docs.upsertByPath(proposalDocPath(d.name), proposalDoc(d, t))
      void queryClient.invalidateQueries({ queryKey: ['docs'] })
    } catch {
      // The draft is still on this device; the next step tries again.
    }
  }, [t, queryClient])

  const send = async (key: string, values: Record<string, string>, waiting: Waiting, repositoryId: string | null, body?: string) => {
    const prompt = catalog?.prompts.find((p) => p.key === key)
    if (!prompt) {
      setProblem(t('startProject.error.noPrompt'))
      return false
    }
    setProblem(null)
    try {
      const order = await api.workOrders.create({
        kind: prompt.kind,
        instructions: body ?? fitPrompt(prompt.body, values),
        title: localized(prompt.title, locale),
        repositoryId,
        maxIterations: null,
      })
      patch({ waiting })
      run.follow(order.id)
      return true
    } catch {
      setProblem(t('run.failed'))
      return false
    }
  }

  const sendInterview = (d: StartDraft) => send('start.interview', { idea: d.idea }, 'interview', d.repositoryId)
  const sendProposal = (d: StartDraft, adjust: string) =>
    send('start.proposal', { idea: d.idea, answers: d.interview ? answersText(d.interview, d.answers) : '', previous: d.proposal ?? '', adjust }, 'proposal', d.repositoryId)
  const sendReview = (d: StartDraft) => send('start.review', { proposal: d.proposal ?? '', proposal_path: proposalDocPath(d.name) }, 'review', d.repositoryId)
  const sendFirst = (d: StartDraft) => send('start.first', { name: d.repositoryName ?? d.name, spec: d.firstSpec ?? '' }, 'first', d.repositoryId)

  const finished = useRef<(order: WorkOrder) => void>(() => {})
  finished.current = (order: WorkOrder) => {
    const d = latest.current
    const text = order.lastReply ?? order.lastReport ?? ''
    if (order.status !== 'Done') {
      patch({ waiting: null })
      setProblem(order.status === 'Stopped' ? t('run.stopped') : t('run.failedRun'))
      return
    }
    if (d.waiting === 'interview') {
      const interview = interviewOf(text)
      if (!interview) {
        patch({ waiting: null })
        setProblem(t('startProject.error.unreadable'))
        return
      }
      const next: StartDraft = { ...d, waiting: null, interview, answers: initialAnswers(interview), name: d.name || interview.name || slugify(d.idea) || 'projeto', step: 'questions' }
      set(next)
      void persist(next)
    } else if (d.waiting === 'proposal') {
      const { markdown, summary } = proposalOf(text)
      if (!markdown) {
        patch({ waiting: null })
        setProblem(t('startProject.error.unreadable'))
        return
      }
      const next: StartDraft = { ...d, waiting: null, proposal: markdown, summary, review: null, reviewOk: null }
      set(next)
      void persist(next)
      // The proposal is checked against the audits' rules before anything starts.
      void sendReview(next)
    } else if (d.waiting === 'review') {
      const review = reviewOf(text)
      const next: StartDraft = { ...d, waiting: null, review: review.markdown, reviewOk: review.ok }
      set(next)
      void persist(next)
    } else if (d.waiting === 'docs') {
      const next: StartDraft = { ...d, waiting: null, firstSpec: firstSpecOf(text) }
      set(next)
      void sendFirst(next).then((sent) => sent && patch({ firstSent: true }))
    } else if (d.waiting === 'first') {
      patch({ waiting: null, result: text.replace(/DEVPULSE-STATUS:.*$/gim, '').trim() })
    }
  }
  const run = useRepositoryRun(RUN_KEY, useCallback((order: WorkOrder) => finished.current(order), []), 'start')

  const stop = useMutation({
    mutationFn: async () => {
      if (run.order) await api.workOrders.stop(run.order.id)
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['run-order'] }),
  })

  /** The same step again, when it failed or stopped being followed (another tab, another device). */
  const retry = () => {
    const d = latest.current
    const what = d.waiting ?? (d.step === 'questions' || d.step === 'idea' ? 'interview' : d.step === 'proposal' ? (d.proposal ? 'review' : 'proposal') : null)
    if (what === 'interview') void sendInterview(d)
    else if (what === 'proposal') void sendProposal(d, '')
    else if (what === 'review') void sendReview(d)
    else if (what === 'first') void sendFirst(d)
    else patch({ waiting: null })
  }

  // A project of this flow, made in the desktop app: the folder, git, the link, the active repository.
  const create = async (name: string, teamId: string, onGithub = false): Promise<RepositoryLink | null> => {
    setProblem(null)
    if (!validProjectName(name)) {
      setProblem(t('startProject.error.name'))
      return null
    }
    let project
    try {
      project = await projectCreate(name, latest.current.interview?.summary ?? '')
    } catch (error) {
      const text = String(error)
      setProblem(text.includes('exists') ? t('startProject.error.exists') : text.includes('invalid-name') ? t('startProject.error.name') : t('startProject.error.create'))
      return null
    }
    if (!project) return null
    // On GitHub too, private, with the person's own login on this computer (docs/refactor/58). If it does not work,
    // the project stays here, as it would have without the option, and the screen says why.
    let remoteUrl = project.remote_url
    if (onGithub) {
      try {
        remoteUrl = await projectPublishGithub(project.path, true)
      } catch (error) {
        const why = String(error)
        setGithubNote(t(why.includes('name-taken') ? 'startProject.githubTaken' : why.includes('gh-signed-out') ? 'startProject.githubSignedOut' : 'startProject.githubFailed'))
      }
    }
    try {
      const repository = await api.repositories.link({
        remoteUrl,
        name: project.name,
        defaultBranch: project.branch && project.branch !== 'HEAD' ? project.branch : 'main',
        teamId: teamId || null,
        projectId: null,
      })
      rememberFolder(repository.name, project.path)
      choose(repository.id)
      askSensitive('project')
      for (const key of ['repositories', 'repository-projects', 'open-projects', 'workspace-folders', 'repo-files']) void queryClient.invalidateQueries({ queryKey: [key] })
      const next: StartDraft = { ...latest.current, name: repository.name, repositoryId: repository.id, repositoryName: repository.name }
      set(next)
      void persist(next)
      return repository
    } catch (error) {
      const known = ['team_required', 'invalid_name', 'required']
      setProblem(error instanceof ApiError && error.code && known.includes(error.code) ? t(`repos.error.${error.code}`) : t('startProject.error.link'))
      return null
    }
  }

  /** start.docs, filled from the flow: the interview, the proposal, the review, and what goes to the pending list. */
  const docsBody = () => {
    const d = latest.current
    const prompt = catalog?.prompts.find((p) => p.key === 'start.docs')
    if (!prompt || !d.interview) return ''
    const pending = pendingItems(d.interview, d.answers, t)
    return fitPrompt(prompt.body, {
      name: d.repositoryName ?? d.name,
      idea: d.idea,
      answers: answersText(d.interview, d.answers),
      proposal: d.proposal ?? '',
      review: d.review ?? '',
      pending: pending.length ? pending.map((p) => `- [ ] ${p}`).join('\n') : '-',
      interview_path: interviewDocPath(d.name),
      proposal_path: proposalDocPath(d.name),
    })
  }

  const desktop = isDesktop()
  // Nothing on this computer for the agent to run in yet: a brand-new person's first prompt would wait forever.
  const nowhereToRun = desktop && !draft.repositoryId && !linked.some((r) => folderFor(r.name))
  const busy = draft.waiting !== null

  const restart = () => {
    clearDraft()
    set(emptyDraft())
    setProblem(null)
  }

  return (
    <div className="stack start-project">
      <header className="start-project__head">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="start-project__title">{t('startProject.title')}</h1>
          <p className="small muted" style={{ margin: 0 }}>{t('startProject.intro')}</p>
        </div>
        {draft.step !== 'idea' && !busy && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={restart}><RotateCcw size={13} /> {t('startProject.restart')}</button>
        )}
      </header>

      <ol className="start-steps" aria-label={t('startProject.steps')}>
        {STEPS.map((step, index) => {
          const current = STEPS.indexOf(draft.step)
          const done = index < current
          return (
            <li key={step} className={`start-steps__item${step === draft.step ? ' is-current' : ''}${done ? ' is-done' : ''}`} aria-current={step === draft.step ? 'step' : undefined}>
              <button type="button" disabled={!done || busy} onClick={() => patch({ step })}>
                <span className="start-steps__mark" aria-hidden>{done ? <Check size={12} /> : index + 1}</span>
                {t(`startProject.step.${step}`)}
              </button>
            </li>
          )
        })}
      </ol>

      {problem && (
        <div className="notice small start-project__problem" role="alert">
          <span>{problem}</span>
          {!busy && <button type="button" className="btn btn--sm" onClick={retry}><RotateCcw size={13} /> {t('startProject.retry')}</button>}
        </div>
      )}
      {/* The project went on without GitHub: said, and kept while the next steps run (they clear the problem line). */}
      {githubNote && <div className="notice small" role="status">{githubNote}</div>}

      {busy && (
        <WaitingPanel
          waiting={draft.waiting!}
          phase={run.phase}
          onStop={() => stop.mutate()}
          onRetry={retry}
          nowhereToRun={nowhereToRun}
          suggestedName={draft.name || slugify(draft.idea)}
          onCreate={(name) => void create(name, '')}
        />
      )}

      {draft.step === 'idea' && (
        <IdeaStep
          idea={draft.idea}
          // The questions come from the server's catalog: nothing to send until it is here.
          disabled={busy || !catalog}
          onChange={(idea) => patch({ idea })}
          onSubmit={() => void sendInterview(latest.current)}
          onResume={(resumed) => set(resumed)}
        />
      )}

      {draft.step === 'questions' && draft.interview && (
        <QuestionsStep
          draft={draft}
          disabled={busy}
          onAnswers={(answers) => patch({ answers })}
          onBack={() => patch({ step: 'idea' })}
          onNext={() => {
            const next: StartDraft = { ...latest.current, step: 'proposal', proposal: null, summary: null, review: null, reviewOk: null }
            set(next)
            void persist(next)
            void sendProposal(next, '')
          }}
        />
      )}

      {draft.step === 'proposal' && (
        <ProposalStep
          draft={draft}
          disabled={busy}
          onAdjust={(adjust) => void sendProposal(latest.current, adjust)}
          onBack={() => patch({ step: 'questions' })}
          onApprove={() => patch({ step: 'start' })}
        />
      )}

      {draft.step === 'start' && (
        <StartStepView
          draft={draft}
          disabled={busy}
          desktop={desktop}
          linked={linked}
          docsBody={docsBody}
          onName={(name) => patch({ name })}
          onUseRepository={(repository) => {
            const next: StartDraft = { ...latest.current, repositoryId: repository.id, repositoryName: repository.name }
            set(next)
            void persist(next)
          }}
          onBegin={async (name, teamId, edited, onGithub) => {
            let d = latest.current
            if (!d.repositoryId) {
              if (!(await create(name, teamId, onGithub))) return
              d = latest.current
            }
            // Unless the person edited it, the prompt is filled now, with the repository's own name.
            const body = edited ?? docsBody()
            if (!body) {
              setProblem(t('startProject.error.noPrompt'))
              return
            }
            const sent = await send('start.docs', {}, 'docs', d.repositoryId, body)
            if (sent) {
              patch({ docsSent: true })
              void persist(latest.current)
            }
          }}
        />
      )}
    </div>
  )
}

// ------------------------------------------------------------------------------ waiting

function WaitingPanel({ waiting, phase, onStop, onRetry, nowhereToRun, suggestedName, onCreate }: {
  waiting: Exclude<Waiting, null>
  phase: 'idle' | 'waiting' | 'working'
  onStop: () => void
  onRetry: () => void
  nowhereToRun: boolean
  suggestedName: string
  onCreate: (name: string) => void
}) {
  const { t } = useI18n()
  const [name, setName] = useState(suggestedName)
  return (
    <section className="panel start-wait" role="status" aria-live="polite">
      <div className="row row--tight">
        {phase === 'working' ? <Loader2 size={16} className="spin" /> : <Hourglass size={16} className="run-bar__hourglass" />}
        <strong className="small">{t(`startProject.waiting.${waiting}`)}</strong>
        <span className="tiny muted">{phase === 'working' ? t('project.menu.working') : t('project.menu.waiting')}</span>
        <div className="spacer" />
        {phase === 'idle' ? (
          <button type="button" className="btn btn--sm" onClick={onRetry}><RotateCcw size={13} /> {t('startProject.retry')}</button>
        ) : (
          <button type="button" className="btn btn--sm btn--ghost" onClick={onStop}><Square size={11} fill="currentColor" /> {t('run.stop')}</button>
        )}
      </div>
      {phase === 'idle' && <p className="tiny muted" style={{ margin: 0 }}>{t('startProject.lost')}</p>}
      {phase === 'waiting' && !nowhereToRun && <p className="tiny muted" style={{ margin: 0 }}>{t('startProject.waitingHint')}</p>}
      {phase === 'waiting' && nowhereToRun && (
        <div className="stack start-wait__folder">
          <p className="tiny" style={{ margin: 0 }}>{t('startProject.nowhereToRun')}</p>
          <div className="row row--tight">
            <input className="input input--sm mono" value={name} onChange={(event) => setName(event.target.value)} aria-label={t('startProject.folderName')} />
            <button type="button" className="btn btn--sm btn--primary" onClick={() => onCreate(name.trim())}><FolderPlus size={13} /> {t('startProject.createFolder')}</button>
          </div>
        </div>
      )}
    </section>
  )
}

// ------------------------------------------------------------------------------ 1. Sua ideia

function IdeaStep({ idea, disabled, onChange, onSubmit, onResume }: {
  idea: string
  disabled: boolean
  onChange: (idea: string) => void
  onSubmit: () => void
  onResume: (draft: StartDraft) => void
}) {
  const { t } = useI18n()
  const { data: tree } = useQuery({ queryKey: ['docs', 'tree'], queryFn: api.docs.tree, staleTime: 60_000 })
  const started = (tree?.docs ?? []).filter((d) => isInterviewDoc(d.path))
  const resume = useMutation({
    mutationFn: async (path: string) => draftFromDoc((await api.docs.byPath(path)).content),
    onSuccess: (resumed) => resumed && onResume(resumed),
  })
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (idea.trim().length >= 10) onSubmit()
  }
  return (
    <form className="panel stack start-card" onSubmit={submit}>
      <label className="stack" style={{ gap: 6 }}>
        <strong>{t('startProject.ideaQuestion')}</strong>
        <span className="small muted">{t('startProject.ideaHint')}</span>
        <textarea
          className="input start-idea"
          rows={7}
          maxLength={4000}
          value={idea}
          disabled={disabled}
          placeholder={t('startProject.ideaPlaceholder')}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
      <div className="row row--between row--wrap">
        <span className="tiny muted">{t('startProject.ideaNext')}</span>
        <button type="submit" className="btn btn--primary" disabled={disabled || idea.trim().length < 10}>
          <Sparkles size={14} /> {t('startProject.ideaSend')}
        </button>
      </div>
      {started.length > 0 && (
        <div className="stack start-resume">
          <strong className="small">{t('startProject.resume')}</strong>
          <ul className="start-resume__list">
            {started.map((doc) => (
              <li key={doc.id}>
                <button type="button" className="btn btn--sm btn--ghost" disabled={disabled || resume.isPending} onClick={() => resume.mutate(doc.path)}>
                  {doc.path.split('/')[1]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  )
}

// ------------------------------------------------------------------------------ 2. Algumas perguntas

function QuestionsStep({ draft, disabled, onAnswers, onBack, onNext }: {
  draft: StartDraft
  disabled: boolean
  onAnswers: (answers: StartDraft['answers']) => void
  onBack: () => void
  onNext: () => void
}) {
  const { t } = useI18n()
  const [page, setPage] = useState(0)
  const interview = draft.interview!
  const answers = draft.answers
  const pages = Math.max(1, Math.ceil(interview.questions.length / PAGE))
  const shown = interview.questions.slice(page * PAGE, page * PAGE + PAGE)
  const assumed = assumedCount(answers)
  const left = unresolved(answers)
  useEffect(() => {
    if (page >= pages) setPage(pages - 1)
  }, [page, pages])

  return (
    <section className="stack start-card">
      {interview.summary && <p className="small" style={{ margin: 0 }}>{interview.summary}</p>}
      <div className="row row--between row--wrap start-legend">
        <span className="row row--tight tiny">
          <span className="start-legend__ai" aria-hidden /> {t('startProject.legendAi')}
          <span className="start-legend__open" aria-hidden /> {t('startProject.legendOpen')}
        </span>
        {assumed > 0 && (
          <button type="button" className="btn btn--sm start-confirm-all" disabled={disabled} onClick={() => onAnswers(confirmAll(answers))}>
            <CheckCheck size={14} /> {t('startProject.confirmAll', { count: assumed })}
          </button>
        )}
      </div>

      {shown.map((question) => (
        <QuestionCard
          key={question.id}
          question={question}
          answer={answers[question.id] ?? { status: 'open', value: null }}
          disabled={disabled}
          onAnswer={(value) => onAnswers(answerOne(answers, question, value))}
          onConfirm={() => onAnswers(confirmOne(answers, question.id))}
          onSkip={() => onAnswers(skipOne(answers, question.id))}
          onDontKnow={() => onAnswers(dontKnow(answers, question))}
        />
      ))}

      {pages > 1 && (
        <div className="row row--tight start-pages">
          <button type="button" className="btn btn--sm btn--ghost" disabled={page === 0} onClick={() => setPage(page - 1)}><ArrowLeft size={13} /> {t('startProject.previous')}</button>
          <span className="tiny muted">{t('startProject.page', { page: page + 1, pages })}</span>
          <button type="button" className="btn btn--sm btn--ghost" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>{t('startProject.more')} <ArrowRight size={13} /></button>
        </div>
      )}

      <div className="row row--between row--wrap">
        <button type="button" className="btn btn--ghost" onClick={onBack} disabled={disabled}><ArrowLeft size={14} /> {t('startProject.step.idea')}</button>
        <div className="row row--tight">
          {left > 0 && <span className="tiny muted" role="status">{t('startProject.left', { count: left })}</span>}
          <button type="button" className="btn btn--primary" disabled={disabled || left > 0} onClick={onNext}>
            {t('startProject.toProposal')} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  )
}

function QuestionCard({ question, answer, disabled, onAnswer, onConfirm, onSkip, onDontKnow }: {
  question: InterviewQuestion
  answer: Answer
  disabled: boolean
  onAnswer: (value: string) => void
  onConfirm: () => void
  onSkip: () => void
  onDontKnow: () => void
}) {
  const { t } = useI18n()
  const [free, setFree] = useState('')
  const fromAi = answer.status === 'assumed' || answer.status === 'confirmed'
  const own = answer.value && !question.options.includes(answer.value) ? answer.value : null
  return (
    <article className={`start-q${fromAi ? ' start-q--ai' : ''} start-q--${answer.status}`} aria-label={question.text} data-testid={`question-${question.id}`}>
      <div className="start-q__head">
        <strong className="small">{question.text}</strong>
        {fromAi && <span className="start-q__badge">{t('startProject.suggested')}</span>}
        {answer.status === 'unknown' && <span className="start-q__badge start-q__badge--decided">{t('startProject.decided')}</span>}
      </div>
      {question.why && <p className="tiny start-q__why">{fromAi ? t('startProject.because', { why: question.why }) : question.why}</p>}
      {question.options.length > 0 && (
        <div className="start-q__options" role="group" aria-label={t('startProject.options')}>
          {question.options.map((option) => (
            <button key={option} type="button" className="start-q__option" aria-pressed={answer.value === option && answer.status !== 'skipped'} disabled={disabled} onClick={() => onAnswer(option)}>
              {option}
            </button>
          ))}
        </div>
      )}
      {own && answer.status !== 'skipped' && <p className="small start-q__value">{answer.status === 'unknown' ? t('startProject.decidedValue', { value: own }) : own}</p>}
      <form
        className="row row--tight start-q__free"
        onSubmit={(event) => {
          event.preventDefault()
          if (!free.trim()) return
          onAnswer(free)
          setFree('')
        }}
      >
        <input className="input input--sm" value={free} disabled={disabled} placeholder={t('startProject.ownAnswer')} aria-label={t('startProject.ownAnswer')} onChange={(event) => setFree(event.target.value)} />
        <button type="submit" className="btn btn--sm" disabled={disabled || !free.trim()}>{t('startProject.answer')}</button>
      </form>
      <div className="row row--tight row--wrap start-q__actions">
        {answer.status === 'assumed' && (
          <button type="button" className="btn btn--sm btn--primary" disabled={disabled} onClick={onConfirm}><Check size={13} /> {t('startProject.confirm')}</button>
        )}
        <button type="button" className="btn btn--sm btn--ghost" disabled={disabled} onClick={onSkip}>{t('startProject.skip')}</button>
        <button type="button" className="btn btn--sm btn--ghost" disabled={disabled} onClick={onDontKnow}>{t('startProject.dontKnow')}</button>
        <span className="tiny muted start-q__state">{t(`startProject.status.${answer.status}`)}</span>
      </div>
    </article>
  )
}

// ------------------------------------------------------------------------------ 3. A proposta

function ProposalStep({ draft, disabled, onAdjust, onBack, onApprove }: {
  draft: StartDraft
  disabled: boolean
  onAdjust: (adjust: string) => void
  onBack: () => void
  onApprove: () => void
}) {
  const { t } = useI18n()
  const [adjust, setAdjust] = useState('')
  if (!draft.proposal) return null
  return (
    <section className="stack start-card">
      <div className="panel project-result__report start-proposal"><Markdown source={draft.proposal} /></div>
      <form
        className="row row--tight row--wrap start-adjust"
        onSubmit={(event) => {
          event.preventDefault()
          if (!adjust.trim()) return
          onAdjust(adjust.trim().slice(0, 600))
          setAdjust('')
        }}
      >
        <input className="input" value={adjust} disabled={disabled} placeholder={t('startProject.adjustPlaceholder')} aria-label={t('startProject.adjust')} onChange={(event) => setAdjust(event.target.value)} />
        <button type="submit" className="btn" disabled={disabled || !adjust.trim()}><RotateCcw size={13} /> {t('startProject.adjust')}</button>
      </form>

      <section className={`panel stack start-review${draft.reviewOk === false ? ' start-review--changes' : ''}`}>
        <strong className="small">{t('startProject.review.title')}</strong>
        {draft.review ? (
          <>
            {draft.reviewOk !== null && <p className="small" style={{ margin: 0 }}>{draft.reviewOk ? t('startProject.review.ok') : t('startProject.review.changes')}</p>}
            <Markdown source={draft.review} />
          </>
        ) : (
          <p className="tiny muted" style={{ margin: 0 }}>{t('startProject.review.pending')}</p>
        )}
      </section>

      <div className="row row--between row--wrap">
        <button type="button" className="btn btn--ghost" disabled={disabled} onClick={onBack}><ArrowLeft size={14} /> {t('startProject.step.questions')}</button>
        <button type="button" className="btn btn--primary" disabled={disabled || !draft.review} onClick={onApprove}>
          {t('startProject.approve')} <ArrowRight size={14} />
        </button>
      </div>
    </section>
  )
}

// ------------------------------------------------------------------------------ 4. Começar

function StartStepView({ draft, disabled, desktop, linked, docsBody, onName, onUseRepository, onBegin }: {
  draft: StartDraft
  disabled: boolean
  desktop: boolean
  linked: RepositoryLink[]
  docsBody: () => string
  onName: (name: string) => void
  onUseRepository: (repository: RepositoryLink) => void
  /** `edited`: the prompt as the person changed it; null to fill it when it is sent. `onGithub`: create it there too. */
  onBegin: (name: string, teamId: string, edited: string | null, onGithub: boolean) => Promise<void>
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: teams } = useQuery({ queryKey: ['repository-teams'], queryFn: api.repositories.teams })
  // The GitHub account this computer is signed in to (gh), if any: the project can go there too (docs/refactor/58).
  const { data: githubLogin } = useQuery({ queryKey: ['github-account'], queryFn: githubAccount, enabled: desktop && !draft.repositoryId, staleTime: 60_000 })
  const [onGithub, setOnGithub] = useState(true)
  const [teamId, setTeamId] = useState('')
  const [edited, setEdited] = useState<string | null>(null)
  const body = edited ?? docsBody()
  const [working, setWorking] = useState(false)
  const needsTeam = !draft.repositoryId && (teams ?? []).length > 0
  const nameOk = validProjectName(draft.name)
  const pending = useMutation({
    mutationFn: () => ensurePendingList(draft.repositoryName ?? draft.name),
    onSuccess: (path) => {
      void queryClient.invalidateQueries({ queryKey: ['docs'] })
      navigate(`/project/docs?path=${encodeURIComponent(path)}`)
    },
  })

  if (draft.docsSent) {
    return (
      <section className="panel stack start-card start-done">
        <strong>{draft.result ? t('startProject.done.finished') : draft.firstSent ? t('startProject.done.first') : t('startProject.done.docs')}</strong>
        <p className="small muted" style={{ margin: 0 }}>{t('startProject.done.where', { name: draft.repositoryName ?? draft.name })}</p>
        {draft.result && <div className="project-result__report"><Markdown source={draft.result} /></div>}
        <div className="row row--tight row--wrap">
          <button type="button" className="btn" onClick={() => pending.mutate()} disabled={pending.isPending}><ClipboardList size={14} /> {t('project.pendingOpen')}</button>
          <Link className="btn btn--ghost" to="/agents/work">{t('startProject.done.follow')}</Link>
        </div>
      </section>
    )
  }

  const canBegin = Boolean(draft.repositoryId) || (desktop && nameOk && (!needsTeam || teamId))
  return (
    <section className="stack start-card">
      {draft.repositoryId ? (
        <p className="small" style={{ margin: 0 }}>{t('startProject.repoReady', { name: draft.repositoryName ?? draft.name })}</p>
      ) : desktop ? (
        <div className="panel stack start-folder">
          <label className="stack" style={{ gap: 4 }}>
            <strong className="small">{t('startProject.folderName')}</strong>
            <input className="input mono" value={draft.name} disabled={disabled} onChange={(event) => onName(event.target.value.trim())} aria-invalid={!nameOk} />
            <span className={`tiny${nameOk ? ' muted' : ' run-bar__notice--bad'}`}>{nameOk ? t('startProject.folderHint') : t('startProject.error.name')}</span>
          </label>
          {needsTeam && (
            <select className="select" value={teamId} onChange={(event) => setTeamId(event.target.value)} aria-label={t('repos.team')}>
              <option value="">{t('repos.teamPick')}</option>
              {(teams ?? []).map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
          )}
          {githubLogin ? (
            <label className="row row--tight small">
              <input type="checkbox" checked={onGithub} disabled={disabled} onChange={(event) => setOnGithub(event.target.checked)} />
              {t('startProject.github', { account: githubLogin })}
            </label>
          ) : (
            <span className="tiny muted">{t('startProject.githubNone')}</span>
          )}
        </div>
      ) : (
        <div className="panel stack start-folder">
          <p className="small" style={{ margin: 0 }}>{t('startProject.webFinish')}</p>
          <Link className="btn btn--sm" to="/agents/connect?tab=desktop" style={{ alignSelf: 'flex-start' }}>{t('openProjects.getDesktop')}</Link>
        </div>
      )}

      {!draft.repositoryId && linked.length > 0 && (
        <label className="stack" style={{ gap: 4 }}>
          <span className="tiny muted">{t('startProject.useLinked')}</span>
          <select className="select select--sm" value="" disabled={disabled} onChange={(event) => {
            const repository = linked.find((r) => r.id === event.target.value)
            if (repository) onUseRepository(repository)
          }}>
            <option value="">{t('startProject.useLinkedPick')}</option>
            {linked.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
      )}

      <details className="start-prompt">
        <summary className="small">{t('startProject.seePrompt')}</summary>
        <p className="tiny muted">{t('startProject.seePromptHint')}</p>
        <textarea className="input mono project-result__fix" rows={10} value={body} onChange={(event) => setEdited(event.target.value)} aria-label={t('startProject.seePrompt')} />
      </details>

      <div className="row row--between row--wrap">
        <span className="tiny muted">{t('startProject.beginHint')}</span>
        <button
          type="button"
          className="btn btn--primary"
          disabled={disabled || working || !canBegin || !body.trim()}
          onClick={() => {
            setWorking(true)
            void onBegin(draft.name, teamId, edited?.trim() || null, Boolean(githubLogin) && onGithub).finally(() => setWorking(false))
          }}
        >
          {working ? <Loader2 size={14} className="spin" /> : draft.repositoryId ? <Send size={14} /> : <FolderPlus size={14} />}{' '}
          {draft.repositoryId ? t('startProject.beginLinked') : t('startProject.begin')}
        </button>
      </div>
    </section>
  )
}
